import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { bulletinMentionOtherSpanSx, bulletinMentionSelfSpanSx } from '../constants/bulletinMentionUi';
import { parseBulletinBodySegments, parseFormattedRuns, type FormattedRun } from '../utils/bulletinBodySegments';

function renderFormattedRuns(runs: FormattedRun[], keyPrefix: string): React.ReactNode[] {
  return runs.map((r, idx) => {
    const k = `${keyPrefix}-${idx}`;
    if (r.kind === 'plain') {
      return <React.Fragment key={k}>{r.text}</React.Fragment>;
    }
    if (r.kind === 'bold') {
      return (
        <Box component="span" key={k} sx={{ fontWeight: 700 }}>
          {renderFormattedRuns(r.children, `${k}-b`)}
        </Box>
      );
    }
    if (r.kind === 'red') {
      return (
        <Box component="span" key={k} sx={{ color: 'error.main' }}>
          {renderFormattedRuns(r.children, `${k}-r`)}
        </Box>
      );
    }
    if (r.kind === 'blue') {
      return (
        <Box component="span" key={k} sx={{ color: 'info.main' }}>
          {renderFormattedRuns(r.children, `${k}-bl`)}
        </Box>
      );
    }
    return (
      <Box component="span" key={k} sx={{ color: 'text.primary' }}>
        {renderFormattedRuns(r.children, `${k}-bk`)}
      </Box>
    );
  });
}

type Props = {
  body: string;
  /** ログイン職員 ID（メンションの自分ハイライト用） */
  myStaffId?: number;
};

/**
 * 掲示板本文: メンション + **太字** + {{red}}{{/red}} + {{blue}}{{/blue}} + {{black}}{{/black}}
 */
const BulletinFormattedBody: React.FC<Props> = ({ body, myStaffId }) => {
  const theme = useTheme();
  const segments = parseBulletinBodySegments(body);
  return (
    <>
      {segments.map((seg, i) =>
        seg.type === 'text' ? (
          <React.Fragment key={i}>{renderFormattedRuns(parseFormattedRuns(seg.text), `t${i}`)}</React.Fragment>
        ) : (
          <Box
            component="span"
            key={i}
            sx={
              myStaffId !== undefined && seg.staffId === myStaffId
                ? bulletinMentionSelfSpanSx()
                : bulletinMentionOtherSpanSx(theme)
            }
          >
            @{seg.displayName}
          </Box>
        )
      )}
    </>
  );
};

export default BulletinFormattedBody;
